import {
  Box,
  Checkbox,
  Flex,
  FlexProps,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  Switch,
  SwitchProps,
} from "@chakra-ui/react";
import { Field, FieldProps } from "formik";
import { useRef } from "react";
import { isNonEmptyString } from "src/services/predicates";

function isTuple<FieldType>(
  value: FieldType | [FieldType, string]
): value is [FieldType, string] {
  return (
    Array.isArray(value) && value.length === 2 && typeof value[1] === "string"
  );
}

export type FormikSwitchInputProps<FieldType> = {
  name: string;
  checked: FieldType | [FieldType, string];
  unchecked?: FieldType | [FieldType, string];
  label?: React.ReactNode;
  formLabelProps?: FormLabelProps;
  containerProps?: FlexProps;
  direction?: "row" | "column";
  variant?: "switch" | "checkbox";
  shouldValidate?: boolean;
  /** Return false to cancel the change before updating Formik. */
  beforeChange?: (args: {
    nextValue: FieldType | undefined;
    currentValue: FieldType;
  }) => boolean | Promise<boolean>;
  description?: React.ReactNode;
  formControlProps?: FormControlProps;
} & SwitchProps;
export function FormikSwitchInput<FieldType, FormType = unknown>({
  name,
  checked,
  unchecked,
  label,
  formLabelProps,
  containerProps,
  id,
  shouldValidate = true,
  variant = "switch",
  description,
  direction = "column",
  formControlProps,
  beforeChange,
  ...props
}: FormikSwitchInputProps<FieldType>) {
  const changePending = useRef(false);
  const [checkedValue, checkedString] = isTuple(checked)
    ? checked
    : [checked, `${checked}`];
  const [uncheckedValue, uncheckedString] = isTuple(unchecked)
    ? unchecked
    : [unchecked, unchecked ? `${unchecked}` : undefined];

  const handleChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    currentValue: FieldType,
    commit: (nextValue: FieldType | undefined) => void
  ) => {
    if (changePending.current) return;

    const nextValue =
      currentValue === checkedValue ? uncheckedValue : checkedValue;
    changePending.current = true;
    try {
      if (beforeChange && !(await beforeChange({ nextValue, currentValue }))) {
        return;
      }
      commit(nextValue);
      props.onChange?.(event);
    } finally {
      changePending.current = false;
    }
  };

  return (
    <Field name={name}>
      {({ form, field, meta }: FieldProps<FieldType, FormType>) => (
        <FormControl
          display="flex"
          isInvalid={isNonEmptyString(meta.error)}
          {...formControlProps}
        >
          <FormLabel
            htmlFor={id ?? name}
            gridColumnGap={3}
            display="grid"
            flexDir={direction === "row" ? "row-reverse" : "column"}
            alignSelf="flex-start"
            alignItems={direction === "row" ? "center" : undefined}
            gridTemplateAreas={
              direction === "row" ? ROW_TEMPLATE_AREAS : COLUMN_TEMPLATE_AREAS
            }
            fontSize={description ? "md" : "sm"}
            {...formLabelProps}
          >
            {label && (
              <Box
                as="span"
                gridArea="label"
                marginBottom={direction === "row" ? undefined : "3"}
              >
                {label}
              </Box>
            )}
            {description ? (
              <Box fontSize="xs" color="blackAlpha.700" gridArea="description">
                {description}
              </Box>
            ) : null}
            <>
              <Flex gridArea="checkbox" {...containerProps}>
                {variant === "switch" ? (
                  <Switch
                    {...props}
                    {...field}
                    id={id ?? name}
                    value={
                      field.value === checkedValue
                        ? checkedString
                        : uncheckedString
                    }
                    isChecked={field.value === checkedValue}
                    onChange={(event) =>
                      handleChange(event, field.value, (nextValue) => {
                        void form.setFieldValue(
                          name,
                          nextValue,
                          shouldValidate
                        );
                      })
                    }
                  />
                ) : (
                  <Checkbox
                    id={id ?? name}
                    value={
                      field.value === checkedValue
                        ? checkedString
                        : uncheckedString
                    }
                    isChecked={field.value === checkedValue}
                    onChange={(event) =>
                      handleChange(event, field.value, (nextValue) => {
                        void form.setFieldValue(
                          name,
                          nextValue,
                          shouldValidate
                        );
                      })
                    }
                  />
                )}
              </Flex>
              <Box fontSize="sm" gridArea="value" marginTop="1">
                {field.value === checkedValue ? checkedString : uncheckedString}
              </Box>
              {isNonEmptyString(meta.error) ? (
                <FormErrorMessage gridArea="error">
                  {meta.error}
                </FormErrorMessage>
              ) : null}
            </>
          </FormLabel>
        </FormControl>
      )}
    </Field>
  );
}

const ROW_TEMPLATE_AREAS = `
 "checkbox label"
 "value description"
 "_ error"
 `;

const COLUMN_TEMPLATE_AREAS = `
"label label"
"description description"
"checkbox _"
"value _"
"error error"
`;
